import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IoClose } from "react-icons/io5";
import { BiBell } from "react-icons/bi";
import {
  getAllNewNotifications,
  marcarLeidoHoy,
  cancelarNotification,
} from "../redux/features/notificationSlice";

/**
 * Avisos "invasivos" de los recordatorios, además de la campanita.
 * Según cómo se creó el recordatorio (campo `presentacion`):
 *   - banner : barra arriba de la pantalla
 *   - popup  : ventana centrada (se muestra de a una para no tapar todo)
 *   - campanita: no se muestra acá, solo en la bandeja
 *
 * La X cierra el aviso solo por ahora (vuelve al recargar). Para que no vuelva hay dos acciones
 * explícitas: "Leído por hoy" (reaparece mañana si es diario) y "Cancelar" (lo desactiva).
 */
function RecordatorioAvisos() {
  const dispatch = useDispatch();
  const notifications = useSelector(getAllNewNotifications);
  const [cerrados, setCerrados] = useState([]);

  const cerrar = (id) => setCerrados((prev) => [...prev, id]);

  const visibles = (notifications ?? []).filter(
    (n) => !n.leido && !cerrados.includes(n.id)
  );
  // "banner" normal y "banner_urgente" (pendiente clínico, parpadea) van los dos como barra arriba.
  const banners = visibles.filter(
    (n) => n.presentacion === "banner" || n.presentacion === "banner_urgente"
  );
  const popup = visibles.find((n) => n.presentacion === "popup");
  const esUrgente = (n) => n.presentacion === "banner_urgente";

  const nombrePaciente = (n) => {
    const p = n?.patient?.persona;
    if (!p) return "";
    return `${p.nombre ?? ""} ${p.apellidos ?? ""}`.trim();
  };

  const Acciones = ({ item, claro }) => (
    <div className="flex items-center gap-3 shrink-0">
      <button
        onClick={() => dispatch(marcarLeidoHoy(item.id))}
        className={`text-xs hover:underline ${
          claro ? "text-white/90" : "text-subMain"
        }`}
      >
        Leído por hoy
      </button>
      <button
        onClick={() => dispatch(cancelarNotification(item.id))}
        className={`text-xs hover:underline ${
          claro ? "text-white/90" : "text-red-500"
        }`}
      >
        Cancelar
      </button>
    </div>
  );

  if (!banners.length && !popup) return null;

  return (
    <>
      {/* Banners apilados arriba */}
      {banners.length > 0 && (
        <div className="w-full flex flex-col gap-2 mb-4">
          {banners.map((item) => (
            <div
              key={item.id}
              className={`w-full flex items-center justify-between gap-4 text-white rounded-lg px-4 py-3 shadow ${
                esUrgente(item)
                  ? "bg-red-600 parpadeo-pendiente"
                  : "bg-subMain"
              }`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <BiBell className="text-xl shrink-0" />
                <p className="text-sm truncate">
                  {item.motivo}
                  {nombrePaciente(item) && (
                    <span className="font-semibold"> — {nombrePaciente(item)}</span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <Acciones item={item} claro />
                <button
                  onClick={() => cerrar(item.id)}
                  aria-label="Cerrar aviso"
                  className="text-white hover:opacity-70"
                >
                  <IoClose className="text-xl" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Popup centrado (uno a la vez) */}
      {popup && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6 relative">
            <button
              onClick={() => cerrar(popup.id)}
              aria-label="Cerrar aviso"
              className="absolute top-4 right-4 text-textGray hover:text-red-500"
            >
              <IoClose className="text-2xl" />
            </button>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-text text-subMain flex items-center justify-center">
                <BiBell className="text-xl" />
              </div>
              <h2 className="text-lg font-semibold text-main">Recordatorio</h2>
            </div>
            <p className="text-sm text-textGray mb-1">{popup.motivo}</p>
            {nombrePaciente(popup) && (
              <p className="text-sm font-medium text-main mb-4">
                {nombrePaciente(popup)}
              </p>
            )}
            <div className="flex items-center justify-end gap-4 mt-5">
              <Acciones item={popup} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default RecordatorioAvisos;
